import { Table, TableHeader, TableRow, TableHeaderCell, TableCell, TableColumnSizingOptions, useTableFeatures, useTableColumnSizing_unstable, createTableColumn, TableColumnDefinition, MenuItem } from "@fluentui/react-components";
import React from "react";
import { TableBodyWithLoading } from "s365-dashboard-v2-file-picker";
import ShowLocalTime from "../../components/show-local-time/show-local-time.component";
import { ShareWithMeScenarioResponseModel } from "../../swagger-clients/s365-dashboard-v2-api-clients.service";
import { useLoading } from "../../utils/loading-indicator.component";
import { processServerError } from "../../utils/helpers/error.helper";
import { getDashboardSharingClient } from "../../services/dashboard.service";
import { FileDisplayName } from "../../components/file-display-name/file-display-name.component";
import { ContextMenuType } from "../../files/context-menu/context-menu.component";
import { PersonDisplay } from "../../components/person-display/person-display.component";
import { FilterDetailsModal } from "../../filters/filter-details-modal/filter-details-modal.component";
import { HubConnection } from "@microsoft/signalr";
import { ScenarioDetailsModal } from "../../excel-runner/scenario-details-modal/scenario-details-modal.component";

type SharedWithMeDoETableProps = {
    hubConnection: HubConnection;
}

export const SharedWithMeDoETable: React.FC<SharedWithMeDoETableProps> = (props) => {

    const [isLoading, loadingService] = useLoading();
    const [selectedItem, setSelectedItem] = React.useState<ShareWithMeScenarioResponseModel>();
    const [showDetailsModal, setShowDetailsModal] = React.useState<boolean>(false);
    const [items, setItems] = React.useState<ShareWithMeScenarioResponseModel[]>([]);
    const contextMenuRef = React.createRef<ContextMenuType>();

    React.useEffect(() => {
        getSharedExperiments();
    }, []);


    const [columnSizingOptions] = React.useState<TableColumnSizingOptions>({
        name: {
            minWidth: 100,
            defaultWidth: 400,
        },
        flowsheet: {
            minWidth: 300,
            defaultWidth: 400
        },
        sharedBy: {
            minWidth: 200,
            defaultWidth: 300
        },
        created: {
            minWidth: 100,
            defaultWidth: 200

        }
    });


    const getSharedExperiments = async () => {
        loadingService.showLoading("Loading experiments...", async (hideMessage) => {

            try {
                const client = getDashboardSharingClient();
                const experimentsResp = await client.getSharedWithMeDoEExperiments();
                setItems(experimentsResp ?? []);

            } catch (error) {
                processServerError(error, undefined, "An error occurred while getting experiments that are shared with me.");

            } finally {
                hideMessage();
            }

        });

    }

    const isColumnCentered = (columnId: string) => {

        switch (columnId) {
            case "shared": return true;

            default: return false;
        }
    }

    const { getRows, columnSizing_unstable, tableRef } = useTableFeatures<ShareWithMeScenarioResponseModel>(
        {
            columns,
            items,
        },
        [useTableColumnSizing_unstable({ columnSizingOptions })]
    );


    return <div>
        <Table ref={tableRef} as="table" {...columnSizing_unstable.getTableProps()} >
            <TableHeader>
                <TableRow>
                    {columns.map((column) => (
                        <TableHeaderCell
                            onDragStart={e => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            className={`${isColumnCentered(column.columnId as string) ? 'files-table__cell--center' : ''} files-table__cell--bold`}
                            {...columnSizing_unstable.getTableHeaderCellProps(
                                column.columnId
                            )}
                        >
                            {column.renderHeaderCell()}
                        </TableHeaderCell>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBodyWithLoading isLoading={isLoading}
                columnCount={4} loadingMessage="Loading..."
                itemCount={items ? items.length : 0}
                noItemsMessage="No items found.">
                {items && items.length > 0 && items.map((item) => {
                    return <TableRow
                        key={`scenario-${item.id}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => { setSelectedItem(item); setShowDetailsModal(true); }}
                        className={selectedItem && selectedItem.id == item.id ? "table-row-selected" : undefined}
                        onContextMenu={(ev) => { ev.preventDefault(); setSelectedItem(item); contextMenuRef.current?.showMenu(ev); }}
                    >
                        {columns.map((column) => (
                            <TableCell
                                className={`${isColumnCentered(column.columnId as string) ? ' column--center' : ''}`}
                                {...columnSizing_unstable.getTableCellProps(column.columnId)}
                            >
                                {column.renderCell(item)}
                            </TableCell>
                        ))}

                    </TableRow>
                })}


            </TableBodyWithLoading>
        </Table>
        {showDetailsModal && selectedItem &&
            <ScenarioDetailsModal
                isOpened
                scenarioId={selectedItem.id}
                scenarioName={selectedItem.name}
                hubConnection={props.hubConnection}
                onClose={() => { setShowDetailsModal(false); }}
            />}
    </div>
}
const columns: TableColumnDefinition<ShareWithMeScenarioResponseModel>[] = [
    createTableColumn<ShareWithMeScenarioResponseModel>({
        columnId: "name",
        renderHeaderCell: () => <>Name</>,
        renderCell: (item: ShareWithMeScenarioResponseModel) => {
            return <span>{item.name}</span>;

        }
    }),
    createTableColumn<ShareWithMeScenarioResponseModel>({
        columnId: "flowsheet",
        renderHeaderCell: () => <>Flowsheet</>,
        renderCell: (item: ShareWithMeScenarioResponseModel) => {
            return <FileDisplayName fileUniqueIdentifier={item.fileUniqueIdentifier} fileVersionNumber={item.fileVersionNumber} hidePath />;

        }
    }),
    createTableColumn<ShareWithMeScenarioResponseModel>({
        columnId: "sharedBy",
        renderHeaderCell: () => <>Shared by</>,
        renderCell: (item: ShareWithMeScenarioResponseModel) => {

            return <PersonDisplay id={`person-${item.owner?.id}`}
                name={item.owner?.displayName}
                userPrincipalName={item.owner?.userPrincipalName} />;
        }
    }),

    createTableColumn<ShareWithMeScenarioResponseModel>({
        columnId: "shared",
        renderHeaderCell: () => <>Shared</>,
        renderCell: (item: ShareWithMeScenarioResponseModel) => {

            return <ShowLocalTime date={item.sharedAtUtc} multiline />;
        }
    })
];